import { useEffect, useRef, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Group, Text, Box, Paper, Center, ActionIcon, Stack } from '@mantine/core';
import { ArrowLeft, Mic, Play, RotateCcw } from 'lucide-react';
import confetti from 'canvas-confetti';
import { mockSongs } from '../data/mockData';
import { AudioEngine } from '../audio/AudioEngine';
import { isNoteMatch, midiToNoteName } from '../utils/musicUtils';
import styles from './ChallengePage.module.css';


const API_URL = process.env.REACT_APP_API_URL || '';


export default function ChallengePage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [song, setSong] = useState(null);
    const [loading, setLoading] = useState(true);

    const [audioEngine] = useState(() => new AudioEngine());
    const [isRecording, setIsRecording] = useState(false);
    const [gameState, setGameState] = useState('IDLE');

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const [recordedUrl, setRecordedUrl] = useState(null);

    const requestRef = useRef();
    const notesRef = useRef([]);
    const currentNoteIdxRef = useRef(0);
    const holdStartTimeRef = useRef(0);

    const [userPitch, setUserPitch] = useState(0);
    const [currentNoteIndex, setCurrentNoteIndex] = useState(0);
    const [matchProgress, setMatchProgress] = useState(0);

    const Z_SPACING = 500;


    // 데이터 가져오기 useEffect
    useEffect(() => {
        const fetchSongData = async () => {
            try {
                const response = await fetch(`${API_URL}/api/songs/${id}`);
                if (!response.ok) throw new Error('노래를 불러올 수 없습니다.');
                const data = await response.json();
                setSong(data);
            } catch (error) {
                console.error("Fetch error:", error);
                alert("데이터를 불러오는 데 실패했습니다.");
                navigate('/');
            } finally {
                setLoading(false);
            }
        };

        fetchSongData();
    }, [id, navigate]);

    // song 데이터가 로드된 후 notesRef 초기화
    useEffect(() => {
        if (song && song.segments && song.segments[0]) {
            notesRef.current = song.segments[0].notes.map((n, i) => ({
                ...n,
                z: -Z_SPACING * i,
                state: i === 0 ? 'TARGET' : 'WAITING'
            }));
        }
    }, [song]);

    // pitchRange 계산 (song이 로드된 후 계산되도록 useMemo 유지)
    const pitchRange = useMemo(() => {
        if (!song || !song.segments[0]) return { min: 60, max: 72, labels: [] };
        const pitches = song.segments[0].notes.map(n => n.pitch);
        const min = Math.min(...pitches) - 2;
        const max = Math.max(...pitches) + 2;
        const labels = [];
        for (let i = max; i >= min; i--) {
            labels.push({ midi: i, name: midiToNoteName(i) });
        }
        return { min, max, labels };
    }, [song]);





    const startGame = async () => {
        try {
            if (audioEngine.context && audioEngine.context.state === 'suspended') {
                await audioEngine.context.resume();
            }
            let stream = await audioEngine.startMicrophone();
            if (!stream) stream = audioEngine.stream || audioEngine.micStream;
            if (!stream) stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            audioChunksRef.current = [];
            mediaRecorderRef.current = new MediaRecorder(stream);
            mediaRecorderRef.current.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunksRef.current.push(e.data);
            };
            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                setRecordedUrl(URL.createObjectURL(blob));
            };
            mediaRecorderRef.current.start();

            setIsRecording(true);
            setGameState('PLAYING');
            currentNoteIdxRef.current = 0;
            setCurrentNoteIndex(0);
            holdStartTimeRef.current = 0;
            setRecordedUrl(null);
            setUserPitch(0);

            notesRef.current.forEach((n, i) => {
                n.z = -(i * Z_SPACING);
                n.state = i === 0 ? 'TARGET' : 'WAITING';
            });

            requestRef.current = requestAnimationFrame(gameLoop);
        } catch (e) {
            console.error("Game Start Error:", e);
            alert("마이크 연결 실패: " + e.message);
        }
    };

    const stopGame = (finished = false) => {
        audioEngine.stopMicrophone();
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        setIsRecording(false);
        if (finished) {
            setGameState('FINISHED');
            confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
        } else {
            setGameState('IDLE');
        }
        if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };

    const gameLoop = (time) => {
        const pitchHz = audioEngine.getPitch();
        const userMidi = AudioEngine.freqToMidi(pitchHz);
        setUserPitch(userMidi > 0 ? userMidi : 0);

        const idx = currentNoteIdxRef.current;
        const targetNote = notesRef.current[idx];

        if (!targetNote && idx >= notesRef.current.length) {
            stopGame(true);
            return;
        }

        if (targetNote) {
            let isMatch = userMidi > 0 && isNoteMatch(targetNote.pitch, userMidi);
            if (isMatch) {
                if (!holdStartTimeRef.current) holdStartTimeRef.current = time;
                const elapsed = time - holdStartTimeRef.current;
                setMatchProgress(Math.min(100, (elapsed / 700) * 100));
                if (elapsed >= 700) {
                    targetNote.state = 'CLEARED';
                    currentNoteIdxRef.current++;
                    setCurrentNoteIndex(currentNoteIdxRef.current);
                    holdStartTimeRef.current = 0;
                    setMatchProgress(0);
                }
            } else {
                holdStartTimeRef.current = 0;
                setMatchProgress(0);
            }
        }

        notesRef.current.forEach((n, i) => {
            const el = document.getElementById(`note-card-${i}`);
            if (!el) return;
            const queuePos = i - currentNoteIdxRef.current;
            const targetZ = n.state === 'CLEARED' ? 1200 : -(queuePos * Z_SPACING);
            n.z += (targetZ - n.z) * 0.12;
            el.style.transform = `translate3d(-50%, -50%, ${n.z}px)`;

            if (n.z > 400) {
                el.style.opacity = 0;
            } else {
                el.style.opacity = i === currentNoteIdxRef.current ? 0.85 : 0.4;
            }
        });

        requestRef.current = requestAnimationFrame(gameLoop);
    };

    const getPitchY = (midi) => {
        const pct = (midi - pitchRange.min) / (pitchRange.max - pitchRange.min);
        return (1 - Math.max(0, Math.min(1, pct))) * 80 + 10;
    };

    if (loading) return <Center h="100vh"><Text c="white">Loading...</Text></Center>;
    if (!song) return null;

    return (
        <Box className={styles.sceneContainer}>
            <Group pos="absolute" top={20} left={20} className={styles.headerGroup}>
                <ActionIcon variant="subtle" onClick={() => navigate('/')} color="white"><ArrowLeft /></ActionIcon>
                <Text c="white" fw={700} size="sm" style={{ opacity: 0.7 }}>{song.title}</Text>
            </Group>

            <div className={styles.roadContainer}>
                <svg width="100%" height="100%" preserveAspectRatio="none" className={styles.roadSvg}>
                    <line x1="50%" y1="0" x2="0" y2="100%" className={styles.roadSvgLine} />
                    <line x1="50%" y1="0" x2="100%" y2="100%" className={styles.roadSvgLine} />
                </svg>
            </div>

            <div className={styles.perspectiveView}>
                {song.segments[0].notes.map((note, i) => {
                    const isActive = i === currentNoteIndex;
                    const targetTop = getPitchY(note.pitch);

                    return (
                        <div
                            key={i}
                            id={`note-card-${i}`}
                            className={styles.noteCard}
                            style={{
                                border: `3px solid ${isActive ? '#fa5252' : '#be4bdb'}`,
                                transform: `translate3d(-50%, -50%, ${notesRef.current[i]?.z || 0}px)`,
                                zIndex: 100 - i,
                            }}
                        >
                            <div className={styles.lyricBox}>
                                <Text size="6rem" fw={900} style={{ lineHeight: 1 }}>{note.lyric}</Text>
                                <Text size="2rem" c="dimmed" fw={700}>{midiToNoteName(note.pitch)}</Text>
                            </div>

                            <div className={styles.pitchLabelsContainer}>
                                {pitchRange.labels.map(l => (
                                    <Text key={l.midi} size="xs" c={l.midi === note.pitch ? "white" : "dimmed"} fw={l.midi === note.pitch ? 900 : 400} style={{ fontSize: '10px' }}>
                                        {l.name}
                                    </Text>
                                ))}
                            </div>

                            <div className={styles.verticalLine}>
                                <div className={styles.targetCircle} style={{
                                    top: `${targetTop}%`,
                                    background: isActive && matchProgress > 0 ? '#40c057' : 'transparent',
                                }} />
                            </div>

                            {isActive && userPitch > 0 && (
                                <div className={styles.userPitchCursor} style={{
                                    top: `${getPitchY(userPitch)}%`
                                }} />
                            )}
                        </div>
                    );
                })}
            </div>

            {gameState === 'IDLE' && (
                <Center pos="absolute" inset={0} style={{ zIndex: 200 }}>
                    <Button
                        size="xl" radius="xl" color="violet"
                        leftSection={<Mic />}
                        onClick={startGame}
                        className={styles.startButton}
                    >
                        시작하기
                    </Button>
                </Center>
            )}

            {gameState === 'FINISHED' && (
                <Center pos="absolute" inset={0} className={styles.finishedOverlay}>
                    <Paper p="40px" radius="xl" bg="dark.7" withBorder shadow="2xl">
                        <Stack align="center">
                            <Text size="32px" fw={900} variant="gradient" gradient={{ from: 'violet', to: 'cyan' }}>축하합니다! 🎉</Text>
                            {recordedUrl && (
                                <Button leftSection={<Play size={20} />} variant="light" color="green" size="lg" onClick={() => new Audio(recordedUrl).play()}>
                                    내 노래 들어보기
                                </Button>
                            )}
                            <Group mt="xl">
                                <Button leftSection={<RotateCcw size={18} />} variant="default" size="md" onClick={startGame}>다시하기</Button>
                                <Button color="violet" size="md" onClick={() => navigate('/')}>목록으로</Button>
                            </Group>
                        </Stack>
                    </Paper>
                </Center>
            )}
        </Box>
    );
}